import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {AgentUserTypeValue, InvalidAgentUserType, ValidAgentUserType} from 'Users/models/User/AgentUserType';
import {InvalidBosToolsUserType, ValidBosToolsUserType} from 'Users/models/User/BosToolsUserType';
import {
  InvalidEnrollmentTrackingUserType,
  ValidEnrollmentTrackingUserType,
} from 'Users/models/User/EnrollmentTrackingUserType';
import {InvalidBeneficiariesUserType, ValidBeneficiariesUserType} from './BeneficiariesUserType';
import {InvalidMessagingUserType, ValidMessagingUserType} from './MessagingUserType';
import {UserTypeClaimsUnion, UserTypeClaimKey} from '../Claims';
import {InvalidAppEntryUserType, ValidAppEntryUserType} from 'Users/models/User/AppEntryUserType';

export type InvalidUserTypeValue =
  | InvalidAgentUserType
  | InvalidEnrollmentTrackingUserType
  | InvalidBosToolsUserType
  | InvalidBeneficiariesUserType
  | InvalidMessagingUserType
  | InvalidAppEntryUserType;

export type ValidUserTypeValue =
  | ValidAgentUserType
  | ValidEnrollmentTrackingUserType
  | ValidBosToolsUserType
  | ValidBeneficiariesUserType
  | ValidMessagingUserType
  | ValidAppEntryUserType;

type UserTypeValue = InvalidUserType['value'] | ValidUserType['value'];

const isValidUserType = (userTypeValue: UserTypeValue): userTypeValue is ValidUserTypeValue =>
  userTypeValue.key === 'ValidAgentUserType' ||
  userTypeValue.key === 'ValidEnrollmentTrackingUserType' ||
  userTypeValue.key === 'ValidBosToolsUserType' ||
  userTypeValue.key === 'ValidBeneficiariesUserType' ||
  userTypeValue.key === 'ValidAppEntryUserType' ||
  userTypeValue.key === 'ValidMessagingUserType';

export class InvalidUserType {
  public readonly key = 'InvalidUserType';

  constructor(public readonly value: InvalidUserTypeValue) {}
}

export class ValidUserType {
  public static create(userTypeValue: UserTypeValue): UserType {
    return isValidUserType(userTypeValue) ? new ValidUserType(userTypeValue) : new InvalidUserType(userTypeValue);
  }

  public readonly key = 'ValidUserType';
  private constructor(public readonly value: ValidUserTypeValue) {}
}

export type UserType = ValidUserType | InvalidUserType;

export type UserTypeOptions<TReturn> = ToMatchOptions<UserType, 'key', TReturn>;

export const areAllUserTypesValid = (userTypes: UserType[]): userTypes is ValidUserType[] =>
  userTypes.every(ut => ut.key === 'ValidUserType');

export const AllowedExternalUserTypes = ['ValidAgentUserType', 'ValidEnrollmentTrackingUserType'];
export const AllowedExternalAgentUserTypes = ['ValidAgentUserType', 'ValidAgentSupportUserType'];
export const isAgentUserTypesAllowedForExternal = (agentUserType: AgentUserTypeValue) =>
  AllowedExternalAgentUserTypes.includes(agentUserType.key);
export const areAllUserTypesAllowedForExternal = (userTypes: ValidUserType[]) =>
  userTypes.every(
    ut =>
      AllowedExternalUserTypes.includes(ut.value.key) &&
      (ut.value.key !== 'ValidAgentUserType' || isAgentUserTypesAllowedForExternal(ut.value.value))
  );

export const matchUserType = <TReturn>(userType: UserType, options: UserTypeOptions<TReturn>): TReturn => {
  switch (userType.key) {
    case 'InvalidUserType':
      return options.InvalidUserType(userType);
    case 'ValidUserType':
      return options.ValidUserType(userType);
  }
};

type ExhaustiveUserTypeOptions<TReturn> = {
  Agent: (agentUserType: InvalidAgentUserType | ValidAgentUserType) => TReturn;
  EnrollmentTracking: (
    enrollmentTrackingUserType: InvalidEnrollmentTrackingUserType | ValidEnrollmentTrackingUserType
  ) => TReturn;
  BosTools: (bosToolsUserType: InvalidBosToolsUserType | ValidBosToolsUserType) => TReturn;
  Beneficiaries: (beneficiariesUserType: InvalidBeneficiariesUserType | ValidBeneficiariesUserType) => TReturn;
  Messaging: (messagingUserType: InvalidMessagingUserType | ValidMessagingUserType) => TReturn;
  AppEntry: (appEntryUserType: InvalidAppEntryUserType | ValidAppEntryUserType) => TReturn;
};

type UserTypeOptionsWithDefault<TReturn> = {
  Agent?: (agentUserType: InvalidAgentUserType | ValidAgentUserType) => TReturn;
  EnrollmentTracking?: (
    enrollmentTrackingUserType: InvalidEnrollmentTrackingUserType | ValidEnrollmentTrackingUserType
  ) => TReturn;
  BosTools?: (bosToolsUserType: InvalidBosToolsUserType | ValidBosToolsUserType) => TReturn;
  Beneficiaries?: (beneficiariesUserType: InvalidBeneficiariesUserType | ValidBeneficiariesUserType) => TReturn;
  Messaging?: (messagingUserType: InvalidMessagingUserType | ValidMessagingUserType) => TReturn;
  AppEntry?: (appEntryUserType: InvalidAppEntryUserType | ValidAppEntryUserType) => TReturn;
  Default: (userType: InvalidUserType['value'] | ValidUserType['value']) => TReturn;
};

export type LineOfBusinessUserTypeOptions<TReturn> =
  | ExhaustiveUserTypeOptions<TReturn>
  | UserTypeOptionsWithDefault<TReturn>;

export const matchUserTypeByLineOfBusiness = <TReturn>(
  userType: InvalidUserType['value'] | ValidUserType['value'],
  options: LineOfBusinessUserTypeOptions<TReturn>
): TReturn => {
  switch (userType.key) {
    case 'ValidAgentUserType':
    case 'InvalidAgentUserType':
      return options.Agent ? options.Agent(userType) : (options as any).Default!(userType);
    case 'InvalidEnrollmentTrackingUserType':
    case 'ValidEnrollmentTrackingUserType':
      return options.EnrollmentTracking ? options.EnrollmentTracking(userType) : (options as any).Default!(userType);
    case 'InvalidBosToolsUserType':
    case 'ValidBosToolsUserType':
      return options.BosTools ? options.BosTools(userType) : (options as any).Default!(userType);
    case 'InvalidBeneficiariesUserType':
    case 'ValidBeneficiariesUserType':
      return options.Beneficiaries ? options.Beneficiaries(userType) : (options as any).Default!(userType);
    case 'InvalidMessagingUserType':
    case 'ValidMessagingUserType':
      return options.Messaging ? options.Messaging(userType) : (options as any).Default!(userType);
    case 'InvalidAppEntryUserType':
    case 'ValidAppEntryUserType':
      return options.AppEntry ? options.AppEntry(userType) : (options as any).Default!(userType);
    default:
      return (options as any).Default ? (options as any).Default(userType) : userType;
  }
};

const negate = <TInput>(predicate: (value: TInput) => boolean) => (value: TInput) => !predicate(value);

export const isAgentUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidAgentUserType' || userType.key === 'InvalidAgentUserType';
export const isEnrollmentTrackingUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidEnrollmentTrackingUserType' || userType.key === 'InvalidEnrollmentTrackingUserType';
export const isBosToolsUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidBosToolsUserType' || userType.key === 'InvalidBosToolsUserType';
export const isBeneficiaryUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidBeneficiariesUserType' || userType.key === 'InvalidBeneficiariesUserType';
export const isMessagingUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidMessagingUserType' || userType.key === 'InvalidMessagingUserType';
export const isAppEntryUserType = (userType: InvalidUserType['value'] | ValidUserType['value']) =>
  userType.key === 'ValidMessagingUserType' || userType.key === 'InvalidMessagingUserType';

export const isNotAgentUserType = negate(isAgentUserType);
export const isNotEnrollmentTrackingUserType = negate(isEnrollmentTrackingUserType);
export const isNotBosToolsUserType = negate(isBosToolsUserType);
export const isNotBeneficiaryUserType = negate(isBeneficiaryUserType);
export const isNotMessagingUserType = negate(isMessagingUserType);

type ExhaustiveValidUserTypeOptions<TReturn> = {
  Agent: (agentUserType: ValidAgentUserType) => TReturn;
  EnrollmentTracking: (enrollmentTrackingUserType: ValidEnrollmentTrackingUserType) => TReturn;
  BosTools: (bosToolsUserType: ValidBosToolsUserType) => TReturn;
  Beneficiaries: (beneficiariesUserType: ValidBeneficiariesUserType) => TReturn;
  Messaging: (messagingUserType: ValidMessagingUserType) => TReturn;
  AppEntry: (appEntryUserType: ValidAppEntryUserType) => TReturn;
};

type ValidUserTypeOptionsWithDefault<TReturn> = {
  Agent?: (agentUserType: ValidAgentUserType) => TReturn;
  EnrollmentTracking?: (enrollmentTrackingUserType: ValidEnrollmentTrackingUserType) => TReturn;
  BosTools?: (bosToolsUserType: ValidBosToolsUserType) => TReturn;
  Beneficiaries?: (beneficiariesUserType: ValidBeneficiariesUserType) => TReturn;
  Messaging?: (messagingUserType: ValidMessagingUserType) => TReturn;
  AppEntry?: (appEntryUserType: ValidAppEntryUserType) => TReturn;
  Default: (userType: ValidUserType['value']) => TReturn;
};

export type LineOfBusinessValidUserTypeOptions<TReturn> =
  | ExhaustiveValidUserTypeOptions<TReturn>
  | ValidUserTypeOptionsWithDefault<TReturn>;

export const matchValidUserTypeByLineOfBusiness = <TReturn>(
  userType: ValidUserType['value'],
  options: LineOfBusinessValidUserTypeOptions<TReturn>
): TReturn => {
  switch (userType.key) {
    case 'ValidAgentUserType':
      return options.Agent ? options.Agent(userType) : (options as any).Default!(userType);
    case 'ValidEnrollmentTrackingUserType':
      return options.EnrollmentTracking ? options.EnrollmentTracking(userType) : (options as any).Default!(userType);
    case 'ValidBosToolsUserType':
      return options.BosTools ? options.BosTools(userType) : (options as any).Default!(userType);
    case 'ValidBeneficiariesUserType':
      return options.Beneficiaries ? options.Beneficiaries(userType) : (options as any).Default!(userType);
    case 'ValidMessagingUserType':
      return options.Messaging ? options.Messaging(userType) : (options as any).Default!(userType);
    case 'ValidAppEntryUserType':
      return options.AppEntry ? options.AppEntry(userType) : (options as any).Default!(userType);
    default:
      return (options as any).Default ? (options as any).Default(userType) : userType;
  }
};

export type UserTypeClaimKeyOptions<TReturn> = ToMatchOptions<UserTypeClaimsUnion, '$type', TReturn>;
export const matchUserTypeClaimKey = <TReturn>(
  userClaim: UserTypeClaimsUnion,
  options: UserTypeClaimKeyOptions<TReturn>
): TReturn => {
  switch (userClaim.$type) {
    case UserTypeClaimKey.ApplicationEntry:
      return options[
        'Renaissance.Authorization.Claims.ApplicationEntry.ApplicationEntryUserTypeClaim, Renaissance.Authorization'
      ](userClaim);
    case UserTypeClaimKey.AgentSite:
      return options['Renaissance.Authorization.Claims.AgentClaim.AgentUserTypeClaim, Renaissance.Authorization'](
        userClaim
      );
    case UserTypeClaimKey.BOSTools:
      return options[
        'Renaissance.Authorization.Claims.BOSToolsClaim.BusinessOperationsSupportUserTypeClaim, Renaissance.Authorization'
      ](userClaim);
    case UserTypeClaimKey.Beneficiary:
      return options[
        'Renaissance.Authorization.Claims.BeneficiaryClaim.BeneficiaryUserTypeClaim, Renaissance.Authorization'
      ](userClaim);
    case UserTypeClaimKey.EnrollTrack:
      return options[
        'Renaissance.Authorization.Claims.EnrollTrackClaim.EnrollTrackUserTypeClaim, Renaissance.Authorization'
      ](userClaim);
    case UserTypeClaimKey.Messaging:
      return options[
        'Renaissance.Authorization.Claims.MessagingClaim.MessagingUserTypeClaim, Renaissance.Authorization'
      ](userClaim);
    case UserTypeClaimKey.MyAccessPortal:
      return options['Renaissance.Authorization.Claims.PortalClaim.PortalUserTypeClaim, Renaissance.Authorization'](
        userClaim
      );
    default:
      return options.Empty(userClaim);
  }
};
